import { Event } from '@/types/zod.schemas';
import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { toLogicDate } from '@/utils/date-utils';

/**
 * PURE HISTORY COMPUTE LOGIC
 * HARDENED: Standardized on toLogicDate for reliable cross-timezone simulation.
 */

export const HistoryCompute = {
    /**
     * Calculates statistics (streak, volume, etc.) from a history of completed events.
     */
    calculateHistoryStats(allEvents: Event[]) {
        const history = (allEvents || []).filter(e => e.type === EVENT_TYPES.PERFORMANCE && e.status === 'COMPLETED');
        const totalWorkouts = history.length;
        
        const totalVolumeNum = history.reduce((acc, curr) => acc + ((curr.preview_data as any)?.volume || 0), 0);
        const totalVolume = totalVolumeNum >= 1000 ? `${(totalVolumeNum / 1000).toFixed(1)}K` : totalVolumeNum;
        
        if (totalWorkouts === 0) return { streak: 0, weekCount: 0, totalWorkouts: 0, totalVolume: 0 };

        const now = new Date();
        const last7Days = [...Array(7)].map((_, i) => {
            const d = new Date(now);
            d.setDate(d.getDate() - i);
            return toLogicDate(d);
        });

        const weekCount = history.filter(s => {
            const date = toLogicDate(s.end_time || s.start_time);
            return last7Days.includes(date);
        }).length;

        let streak = 0;
        let checkDate = new Date(now);
        const todayStr = toLogicDate(checkDate);
        
        const todayHasLog = history.some(s => toLogicDate(s.end_time || s.start_time) === todayStr);
        if (!todayHasLog) checkDate.setDate(checkDate.getDate() - 1);

        while (true) {
            const dateStr = toLogicDate(checkDate);
            const hasActivity = history.some(s => toLogicDate(s.end_time || s.start_time) === dateStr);
            if (hasActivity) {
                streak++;
                checkDate.setDate(checkDate.getDate() - 1);
            } else break;
            if (streak > 365) break;
        }

        return { streak, weekCount, totalWorkouts, totalVolume };
    },

    /**
     * Filters and maps events for a specific date with temporal states (PAST/PRESENT/FUTURE).
     * FIXED: Passing raw start_time string to toLogicDate to prevent timezone shifting.
     */
    getEventsForDate(allEvents: Event[], dateObj: Date, activePerformance: any, activeRecovery: any) {
        if (!dateObj) return [];
        const selectedStr = toLogicDate(dateObj);
        const now = new Date();
        const todayStr = toLogicDate(now);

        const events = (allEvents || []).filter(e => {
            // CRITICAL: Use the raw start_time string to benefit from toLogicDate's literal split logic.
            // This prevents UTC shifting where 00:00Z becomes 22:00 (yesterday) in local time.
            const localDateStr = toLogicDate(e.start_time);
            if (localDateStr !== selectedStr) return false;
            
            // Filter out events currently being "consumed" by an active session
            if (e.status === 'PLANNED') {
                if (activePerformance?.p_consume_event_id === e.id) return false;
                if (activeRecovery?.p_consume_event_id === e.id) return false;
            }
            return true;
        }).map(e => ({
            ...e,
            temporalState: e.status === 'PLANNED' 
                ? (selectedStr < todayStr ? 'PAST' : (selectedStr > todayStr ? 'FUTURE' : 'PRESENT'))
                : 'PAST'
        }));

        // Inject Virtual Active Events if it's "Today"
        if (selectedStr === todayStr) {
            if (activePerformance) {
                events.push({
                    id: activePerformance.p_consume_event_id || activePerformance.id,
                    type: EVENT_TYPES.PERFORMANCE,
                    status: 'ACTIVE',
                    temporalState: 'PRESENT',
                    start_time: activePerformance.startTime,
                    label: activePerformance.label || 'Activity',
                    summary: 'In Progress...'
                } as any);
            }
            if (activeRecovery) {
                events.push({
                    id: activeRecovery.p_consume_event_id || (activeRecovery as any).id || 'active-recovery',
                    type: EVENT_TYPES.RECOVERY,
                    status: 'ACTIVE',
                    temporalState: 'PRESENT',
                    start_time: activeRecovery.startTime,
                    label: 'Sleep',
                    summary: 'Recording...'
                } as any);
            }
        }

        const uniqueEventsMap = new Map();
        events.forEach(e => {
            if (!uniqueEventsMap.has(e.id) || e.status === 'ACTIVE') {
                uniqueEventsMap.set(e.id, e);
            }
        });

        return Array.from(uniqueEventsMap.values()).sort((a, b) => {
            const timeA = new Date(a.end_time || a.start_time).getTime();
            const timeB = new Date(b.end_time || b.start_time).getTime();
            return timeB - timeA;
        });
    }
};
